import { useEffect } from 'react'
import { usePageVisibility } from 'react-page-visibility'
import { ObservableQuery } from '@apollo/client'

const DEFAULT_POLLING_INTERVAL_MS = 2000

export interface IUseGraphQLPollingProps {
  /**
   * Overrides the set pollInterval on the origianl GraphQL query used with this hook.
   * @default 2000
   */
  pollInterval?: number
  /**
   * A function that instructs the query to begin re-executing at a specified interval (in milliseconds).
   * This function is provided by Apollo Client and accessible through the GraphQL query hooks.
   *
   * @see https://www.apollographql.com/docs/react/data/queries/
   */
  startPolling: ObservableQuery['startPolling']
  /**
   * A function that instructs the query to stop polling after a previous call to startPolling.
   * This function is provided by Apollo Client and accessible through the GraphQL query hooks.
   *
   * @see https://www.apollographql.com/docs/react/data/queries/
   */
  stopPolling: ObservableQuery['stopPolling']
  /**
   * Polling for the hook instance to be enabled or disabled manually
   * @default true
   */
  isPollingEnabled?: boolean
  /**
   * A function that instructs the query to refresh query results.
   * This function is provided by Apollo Client and accessible through the GraphQL query hooks.
   *
   * @see https://www.apollographql.com/docs/react/data/queries/
   */
  refetch?: ObservableQuery['refetch']
  /**
   * Whether or not query results should be refreshed at the start of polling
   * @default false
   */
  refetchWhenStartPolling?: boolean
}

/**
 * This hook can be used in conjunction with GraphQL queries that have polling enabled to provide
 * the ability to disable polling when the Rogers tab issuing the queries is not visible, i.e. not active.
 */
const useGraphQLPolling = (props: IUseGraphQLPollingProps) => {
  const {
    isPollingEnabled = true,
    pollInterval = DEFAULT_POLLING_INTERVAL_MS,
    startPolling,
    stopPolling,
    refetch,
    refetchWhenStartPolling,
  } = props || {}
  const isVisible = usePageVisibility()

  /**
   * Decides if a query polling should continue or stop based on the props provided to the hook
   * and the current visibility state of a the current Rogers' tab.
   */
  useEffect(() => {
    if (isVisible && isPollingEnabled) {
      startPolling(pollInterval)
      if (refetch && refetchWhenStartPolling) {
        refetch()
      }
    } else {
      stopPolling()
    }
  }, [
    isVisible,
    pollInterval,
    startPolling,
    stopPolling,
    isPollingEnabled,
    refetch,
    refetchWhenStartPolling,
  ])
}

export default useGraphQLPolling
